import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import dayjs from '@/shared/lib/dayjs';
import VisitClinicDetailModal from '@/components/organisms/VisitClinicCalendar/VisitClinicDetailModal';
import { LAW_REVISION_DATE_2026, type StagedVisit } from './meisaiEarliestVisit';
import AppButton from '@/components/atoms/AppButton';
import AppTooltip from '@/components/atoms/AppTooltip';
import useAppModal from '@/hooks/useAppModal';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  R2_HOT_TREATMENT_TYPE_ID,
  R2_MASSAGE_PART_OPTIONS,
  R2_ORTHOMORPHIA_PART_OPTIONS,
} from '@/constants/consentForm';
import { resolveR2TreatmentPartLabelsFromStoredConsent } from '@/utils/consentForm';
import { useJpHolidayDates } from '@/services/main/jpHolidays';
import type {
  VisitClinicDayVisit,
  VisitClinicMassager,
  VisitClinicConsent,
  VisitClinicRezept,
  PendingVisitData,
} from '@/types/visitClinic.types';
import styles from './VisitClinicCalendarTable.module.css';

interface Props {
  /** 令和8年法改正 No 5 — needed to read the patient's 明細書発行設定 for the month. */
  patientId?: number | null;
  yearmonth: string; // YYYY-MM
  serviceType: string;
  visits: Record<string, VisitClinicDayVisit>;
  massagers: VisitClinicMassager[];
  mainMassagerId: number | null;
  mainClinicLocationId: number | null;
  outcomeTypes?: Array<{ id: number; name: string }>;
  consents: VisitClinicConsent[];
  /** R2 (HIT-15106) — 料金 tables with their on-screen 同意書と異なる理由, for the 詳細編集 reminder. */
  rezepts?: VisitClinicRezept[];
  initialInspectionPrices?: { single: number; combined: number };
  onChange: (pending: Record<string, PendingVisitData>) => void;
  storeLoading?: boolean;
}

const DAY_OF_WEEK_JP = ['日', '月', '火', '水', '木', '金', '土'];

/** クリック位置 — グレーセルを押したときは自動〇付けしない（非グレーに〇が付くのを防ぐ） */
type CalendarClickRow =
  | 'header'
  | 'initial'
  | 'acupuncture'
  | 'moxibustion'
  | 'electric'
  | 'r2Massage'
  | 'r2MassagePart'
  | 'r2Orthomorphia'
  | 'r2OrthomorphiaPart'
  | 'r2HotCompress'
  | 'r2HotCompressElectro'
  | 'houseCall'
  | 'massager';

// Row header background colors per BD §7 (matches A2 mdb.css)
const ROW_BG: Record<string, string> = {
  initial: '#CCFFFF',
  acupuncture: '#FFFF99',
  moxibustion: '#CCFFCC',
  electric: '#FFCC99',
  houseCall: '#EAEAEA',
};

// R2 row header colors per the BD calendar sample: group cells are colored, part labels stay white
const R2_ROW_BG: Record<string, string> = {
  massageGroup: '#BDD7EE',
  orthomorphiaGroup: '#C6E0B4',
  hotCompress: '#FFCCFF',
  hotCompressElectro: '#F8CBAD',
  houseCall: '#D9D9D9',
};

/** Stacks the characters of an R2 group label and spreads them over the merged cell height (BD sample). */
function R2GroupLabelText({ text }: { text: string }) {
  return (
    <div className={styles.r2GroupLabelText}>
      {Array.from(text).map((ch, i) => (
        <span key={i}>{ch}</span>
      ))}
    </div>
  );
}

function getDaysInMonth(yearmonth: string): number {
  const [year, month] = yearmonth.split('-').map(Number);
  return new Date(year, month, 0).getDate();
}

function buildDate(yearmonth: string, day: number): string {
  return `${yearmonth}-${String(day).padStart(2, '0')}`;
}

function getDayOfWeek(yearmonth: string, day: number): string {
  return DAY_OF_WEEK_JP[new Date(`${yearmonth}-${String(day).padStart(2, '0')}`).getDay()];
}

/**
 * BD §7.4/7.5/7.6: True if consent content allows the treatment
 * (from treatment_content: はり/きゅう/電 — A2 parity). When `date` is outside
 * every consent term, fall back to the month consent list so the cell is not grayed
 * only because it is outside treatment_term_from/to.
 */
function isConsentTreatmentAllowed(
  date: string,
  consents: VisitClinicConsent[],
  field: 'has_acupuncture' | 'has_moxibustion' | 'has_electric'
): boolean {
  if (consents.length === 0) return false;
  const covering = consents.filter(c => {
    const from = c.treatment_term_from;
    const to = c.treatment_term_to;
    return from && to && date >= from && date <= to;
  });
  const targets = covering.length > 0 ? covering : consents;
  return targets.some(c => c[field] === true);
}

/** 人数区分 → 往診セルの丸数字。レセプトPDFの施術日欄凡例（訪問1①〜訪問5⑤）と揃える。 */
const HOUSE_CALL_PEOPLE_MARKS: Record<number, string> = {
  1: '①',
  2: '②',
  3: '③',
  4: '④',
  5: '⑤',
};

/**
 * 令和8年法改正 No 2 / No 6 — 丸数字は人数そのものではなく 訪問施術料の人数区分。
 * backend `HokenConstants::visitTreatmentNumberFor()` と R2 `PDFMakerLogic` の凡例のミラー:
 *   2026-07-01 以降: 1人 / 2人 / 3〜9人 / 10〜19人 / 20人以上 → ①〜⑤
 *   それ以前  : 1人 / 2人 / 3人以上 → ①〜③（旧様式の凡例は③までなので
 *                band4（10人以上）も③に戻す—PDF側と同じ扱い）
 *
 * 0人は「訪問施術料では算定しない」であって区分①ではないので null。
 */
function visitTreatmentPeopleBand(numberOfPeople: number, visitDate: string): number | null {
  if (numberOfPeople <= 0) return null;

  if (visitDate < LAW_REVISION_DATE_2026) {
    return numberOfPeople >= 3 ? 3 : numberOfPeople;
  }

  if (numberOfPeople >= 20) return 5;
  if (numberOfPeople >= 10) return 4;
  if (numberOfPeople >= 3) return 3;
  return numberOfPeople;
}

/**
 * 往診 row mark. Mirrors the 施術録 legend (A2 PDFMakerLogic :1317-1330):
 * ◎ = 突発的訪問 or a 通所-billed visit, ①〜⑤ = 訪問施術料の人数区分 (see
 * visitTreatmentPeopleBand), 〇 otherwise. The ◎ condition itself is computed backend-side.
 */
function getVisitClinicHouseCallMark(
  row: {
    number_of_people?: number | null;
    is_house_call_double_circle?: boolean | null;
  },
  visitDate: string,
): string {
  if (row.is_house_call_double_circle) {
    return '◎';
  }

  const band = visitTreatmentPeopleBand(Number(row.number_of_people ?? 0), visitDate);

  return band === null ? '〇' : HOUSE_CALL_PEOPLE_MARKS[band];
}

function hasR2ConsentValue(value?: string | number | null): boolean {
  if (value === null || value === undefined) return false;
  return String(value)
    .split(',')
    .map(v => v.trim())
    .filter(Boolean)
    .length > 0;
}

/** 日付・曜日テキストの日/土色。祝日は呼び出し側で日曜色を優先する。 */
function weekdayDowTextClass(yearmonth: string, day: number): string {
  const dow = new Date(`${yearmonth}-${String(day).padStart(2, '0')}`).getDay();
  if (dow === 0) return styles.dowSun;
  if (dow === 6) return styles.dowSat;
  return '';
}

/** A2 mdb.css #treatment_table .sunday — 日曜のみ列全体 lightcyan。土曜は曜日行の文字色のみ（列背景なし） */
function sundayColumnBgClass(yearmonth: string, day: number): string {
  const dow = new Date(`${yearmonth}-${String(day).padStart(2, '0')}`).getDay();
  return dow === 0 ? styles.vcSundayColumn : '';
}

// CSS module classes for table headers and cells
const TH_BASE = styles.dayHeader;
const TH_WEEKDAY = styles.dayOfWeek;
const TD_BASE = styles.tdCell;
const TD_LABEL = styles.tdLabel;
const R2_GROUP_LABEL = styles.r2GroupLabel;
const R2_PART_LABEL = styles.r2PartLabel;
const DAY_COL_W = styles.dayColW;

// A2 interaction state classes (using CSS module)
// day-level block (future / calendar blocked) — pointer-events-none
const VC_GRAYOUT = styles.vcGrayout;
// treatment-level gray — still clickable for 日別削除
const VC_GRAYOUT_SOFT = styles.vcGrayoutSoft;
// A2: locked cells have NO special visual in the calendar — they look and behave like normal cells.
// Only the delete action is blocked (shows toast). Detail form disables inputs separately.
const VC_CLICKABLE = styles.vcClickable;
// selected cell — day is active
const VC_SELECTED = styles.vcSelected;
// error cell — validation error
const VC_ERROR = styles.vcError;

function appendSundayColumnBg(parts: string[], yearmonth: string, day: number): void {
  const w = sundayColumnBgClass(yearmonth, day);
  if (!w) return;
  const suppresses =
    parts.includes(VC_SELECTED) ||
    parts.includes(VC_GRAYOUT) ||
    parts.includes(VC_GRAYOUT_SOFT) ||
    parts.includes(VC_ERROR);
  if (!suppresses) parts.push(w);
}

/**
 * Interactive treatment calendar — matches A2 legacy behavior.
 *
 * A2 interaction model:
 * - Hover: any cell (except label column) shows deepskyblue via CSS :hover (instant)
 * - Click day column: selects whole column with .select_active (lightskyblue)
 *   • Empty day → auto-populates all available treatments with 〇
 *   • Day with data → just highlights existing 〇 cells (no data change)
 * - Individual field toggle: only via 詳細 modal (not per-cell click)
 * - Disabled: .grayout class + pointer-events:none (no hover, no click)
 */
const VisitClinicCalendarTable: React.FC<Props> = ({
  patientId,
  yearmonth,
  serviceType,
  visits,
  massagers,
  mainMassagerId,
  mainClinicLocationId,
  outcomeTypes,
  consents,
  rezepts,
  initialInspectionPrices,
  onChange,
  storeLoading,
}) => {
  const [pending, setPending] = useState<Record<string, PendingVisitData>>({});
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const modal = useAppModal();
  // Use JST (Asia/Tokyo) to avoid midnight UTC vs JST date mismatch
  const today = dayjs().tz('Asia/Tokyo').format('YYYY-MM-DD');
  const totalDays = getDaysInMonth(yearmonth);
  const holidayDates = useJpHolidayDates();
  const isR2 = serviceType?.toUpperCase() === SERVICE_TYPE_R2;

  const noMassagerSelected = mainMassagerId === null;
  // License flags are per massager — pick the first entry matching massager_id.
  const mainMassager =
    mainMassagerId !== null
      ? (massagers.find(m => m.massager_id === mainMassagerId) ?? null)
      : null;
  const hasAcupunctureLicense = mainMassager?.has_acupuncture_license ?? true;
  const hasMoxibustionLicense = mainMassager?.has_moxibustion_license ?? true;
  const hasMassageLicense = mainMassager?.has_massage_license ?? true;

  // A2-equivalent: check if consent blocks ALL days for each treatment type
  const allDaysAcupunctureConsentGrayed = useMemo(() => {
    for (let d = 1; d <= totalDays; d++) {
      if (isConsentTreatmentAllowed(buildDate(yearmonth, d), consents, 'has_acupuncture'))
        return false;
    }
    return true;
  }, [yearmonth, totalDays, consents]);

  const allDaysMoxibustionConsentGrayed = useMemo(() => {
    for (let d = 1; d <= totalDays; d++) {
      if (isConsentTreatmentAllowed(buildDate(yearmonth, d), consents, 'has_moxibustion'))
        return false;
    }
    return true;
  }, [yearmonth, totalDays, consents]);

  // A2 callenderDescription: `disabled` only when one license is missing AND every day the *other*
  // row is fully grayout (cannot use はり at all AND cannot use きゅう on any day). Do NOT block the
  // whole calendar just because consent omits はり while きゅう is still allowed — rows gray per-cell.
  const allTreatmentsDisabled =
    isR2
      ? !hasMassageLicense
      : ((!hasAcupunctureLicense && !hasMoxibustionLicense) ||
      (!hasAcupunctureLicense && allDaysMoxibustionConsentGrayed) ||
      (!hasMoxibustionLicense && allDaysAcupunctureConsentGrayed));
  // Use ref (not state) so StrictMode's double-invoked effect sees the
  // updated value synchronously and does not dispatch the modal twice.
  const licenseDialogShownRef = useRef(false);

  // A2 parity: 初検 auto-mark applies only when clicked date exactly equals consent.first_treatment_date (full date),
  // not by day-of-month across different months.
  const isFirstTreatmentDate = useCallback(
    (date: string): boolean =>
      consents.some(c => !!c.first_treatment_date && c.first_treatment_date === date),
    [consents]
  );

  // Reset local pending + selection when massager changes (A2: switching massager discards unsaved calendar data)
  useEffect(() => {
    setPending({});
    setSelectedDay(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainMassagerId]);

  useEffect(() => {
    onChange(pending);
  }, [pending, onChange]);

  useEffect(() => {
    if (!allTreatmentsDisabled) {
      licenseDialogShownRef.current = false;
      return;
    }
    if (licenseDialogShownRef.current) return;
    licenseDialogShownRef.current = true;
    modal.error({
      title: '主な施術師が施術内容に対する免許を所持していないため、予定を入力できません。',
      okText: 'OK',
    });
  }, [allTreatmentsDisabled, modal]);

  const buildDefault = useCallback(
    (existing: VisitClinicDayVisit | null): PendingVisitData => ({
      visit_id: existing?.id ?? null,
      massager_id: existing?.massager_id ?? mainMassagerId ?? 0,
      clinic_location_id: existing?.clinic_location_id ?? mainClinicLocationId ?? null,
      patient_address_id: existing?.patient_address_id ?? 0,
      is_initial_inspection_fee: existing?.is_initial_inspection_fee ?? 0,
      is_acupuncture: existing?.is_acupuncture ?? 0,
      is_moxibustion: existing?.is_moxibustion ?? 0,
      electric_treatment: existing?.electric_treatment ?? 0,
      is_not_hot_treatment: existing?.is_not_hot_treatment ?? false,
      massage: existing?.massage ?? null,
      orthomorphia: existing?.orthomorphia ?? null,
      previous_visit_id: existing?.previous_visit_id ?? null,
      number_of_people: existing?.number_of_people ?? 0,
      is_house_call_double_circle: existing?.is_house_call_double_circle ?? false,
      outcome_type_id: existing?.outcome_type_id ?? null,
      is_insurance: existing?.is_insurance ?? 0,
      massager_remarks: existing?.massager_remarks ?? null,
      // 令和8年法改正 No 5 — seed from the saved visit so the box shows what the server
      // derived; it only becomes a hand-tick once the user changes it.
      is_meisai_issue: existing?.is_meisai_issue ?? 0,
      is_meisai_issue_user_changed: existing?.is_meisai_issue_user_changed ?? 0,
      is_deleted: false,
      is_dirty: false,
    }),
    [mainMassagerId, mainClinicLocationId]
  );

  const getRow = useCallback(
    (day: number): PendingVisitData => {
      const date = buildDate(yearmonth, day);
      const pd = pending[date];
      // BD §7.9: deleted day → all 〇 marks must disappear from calendar
      if (pd?.is_deleted) return { ...buildDefault(null), is_deleted: true, is_dirty: true };
      return pd ?? buildDefault(visits[String(day)] ?? null);
    },
    [pending, visits, yearmonth, buildDefault]
  );

  // 令和8年法改正 No 5 — the month as it stands ON SCREEN, for the 明細書発行加算 checkbox.
  // The server can only answer for saved rows, but this calendar stages a whole month of adds
  // and deletes before 登録: add a day on the 5th while the 6th is already saved and the 6th
  // has to stop offering the box straight away, not after the save. A day with no saved visit
  // has no id yet, so its date stands in — the calendar allows one visit per day, so it is
  // unique within the month either way.
  //
  // 往診 days are left out, to match what the server counts: a row 訪問入力 created cannot
  // carry the 加算, so it must not push a later 来院 out of the "earliest of the month" spot.
  const stagedVisits = useMemo<StagedVisit[]>(
    () =>
      Object.entries(pending)
        .filter(([, pd]) => !pd.is_deleted)
        .filter(([, pd]) => pd.previous_visit_id === null || pd.previous_visit_id === -1)
        .map(([date, pd]) => ({ id: pd.visit_id ?? date, visitDate: date })),
    [pending]
  );

  const stagedDeletedVisitIds = useMemo<number[]>(
    () =>
      Object.values(pending)
        .filter(pd => pd.is_deleted && typeof pd.visit_id === 'number')
        .map(pd => pd.visit_id as number),
    [pending]
  );

  const updateRow = useCallback(
    (day: number, update: Partial<PendingVisitData>) => {
      const date = buildDate(yearmonth, day);
      setPending(prev => {
        const current = prev[date] ?? buildDefault(visits[String(day)] ?? null);
        return { ...prev, [date]: { ...current, ...update, is_dirty: true } };
      });
    },
    [yearmonth, visits, buildDefault]
  );

  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  const isHoliday = useCallback(
    (day: number): boolean => holidayDates.has(buildDate(yearmonth, day)),
    [holidayDates, yearmonth]
  );

  const isGreyed = useCallback(
    (day: number): boolean => {
      const date = buildDate(yearmonth, day);
      return date > today;
    },
    [yearmonth, today]
  );

  const isCalendarBlocked = noMassagerSelected || allTreatmentsDisabled;
  const isLocked = (day: number): boolean => !!visits[String(day)]?.is_locked;
  const isError = (day: number): boolean => !!visits[String(day)]?.is_error;
  const hasData = (day: number): boolean => {
    const date = buildDate(yearmonth, day);
    const pd = pending[date];
    if (pd?.is_deleted) return false; // deleted → treat as empty, allow re-creating
    return !!visits[String(day)] || !!pd;
  };

  const getCoveringR2Consents = useCallback(
    (day: number): VisitClinicConsent[] => {
      const date = buildDate(yearmonth, day);
      return consents.filter(c => {
        const from = c.treatment_term_from;
        const to = c.treatment_term_to;
        return from && to && date >= from && date <= to;
      });
    },
    [yearmonth, consents]
  );

  const hasR2ConsentOnDate = useCallback(
    (day: number): boolean => getCoveringR2Consents(day).length > 0,
    [getCoveringR2Consents]
  );

  const isR2MassageAllowed = useCallback(
    (day: number): boolean => {
      const covering = getCoveringR2Consents(day);
      if (covering.length === 0) return true;
      return covering.some(c => hasR2ConsentValue(c.massage));
    },
    [getCoveringR2Consents]
  );

  // HIT-15106 (BD 1.3.2 S26): a マッサージ limb is open when the 同意書 has it on the
  // 「マッサージ」 row or the 「マッサージ＋変形徒手矯正術」 row (stored in orthomorphia).
  const isR2MassagePartAllowed = useCallback(
    (day: number, part: string): boolean => {
      const covering = getCoveringR2Consents(day);
      if (covering.length === 0) return true;
      return covering.some(c =>
        resolveR2TreatmentPartLabelsFromStoredConsent(c.massage ?? null, c.orthomorphia ?? null).has(part)
      );
    },
    [getCoveringR2Consents]
  );

  const getR2MassagePartSet = useCallback(
    (day: number, row: PendingVisitData): Set<string> => {
      if (row.massage !== null && row.massage !== undefined) {
        return resolveR2TreatmentPartLabelsFromStoredConsent(row.massage, null);
      }
      const out = new Set<string>();
      getCoveringR2Consents(day).forEach(c => {
        resolveR2TreatmentPartLabelsFromStoredConsent(c.massage ?? null, null).forEach(part =>
          out.add(part)
        );
      });
      return out;
    },
    [getCoveringR2Consents]
  );

  const isR2OrthomorphiaAllowed = useCallback(
    (day: number): boolean => {
      const covering = getCoveringR2Consents(day);
      if (covering.length === 0) return true;
      return covering.some(c => hasR2ConsentValue(c.orthomorphia));
    },
    [getCoveringR2Consents]
  );

  const isR2OrthomorphiaPartAllowed = useCallback(
    (day: number, part: string): boolean => {
      const covering = getCoveringR2Consents(day);
      if (covering.length === 0) return true;
      return covering.some(c =>
        resolveR2TreatmentPartLabelsFromStoredConsent(null, c.orthomorphia ?? null).has(part)
      );
    },
    [getCoveringR2Consents]
  );

  const getR2OrthomorphiaPartSet = useCallback(
    (day: number, row: PendingVisitData): Set<string> => {
      if (row.orthomorphia !== null && row.orthomorphia !== undefined) {
        return resolveR2TreatmentPartLabelsFromStoredConsent(null, row.orthomorphia);
      }
      const out = new Set<string>();
      getCoveringR2Consents(day).forEach(c => {
        resolveR2TreatmentPartLabelsFromStoredConsent(null, c.orthomorphia ?? null).forEach(part =>
          out.add(part)
        );
      });
      return out;
    },
    [getCoveringR2Consents]
  );

  const isR2HotTreatmentAllowed = useCallback(
    (day: number, typeId: number): boolean => {
      const covering = getCoveringR2Consents(day);
      if (covering.length === 0) return true;
      return covering.some(c => Number(c.hot_treatment_type_id ?? 0) === typeId);
    },
    [getCoveringR2Consents]
  );

  const isR2CoreTreatmentDisabled = useCallback(
    (day: number): boolean => !isR2MassageAllowed(day) && !isR2OrthomorphiaAllowed(day),
    [isR2MassageAllowed, isR2OrthomorphiaAllowed]
  );

  // Per-row grayout flags (A2 semantics)
  // isAcupunctureGrayed / isMoxibustionGrayed defined first — used by isInitialGrayed, isElectricGrayed, isHouseCallGrayed

  // BD §7.4: disable if no 免許はり OR consent does not include はり treatment content
  const isAcupunctureGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked || !hasAcupunctureLicense) return true;
      const date = buildDate(yearmonth, day);
      return !isConsentTreatmentAllowed(date, consents, 'has_acupuncture');
    },
    [isGreyed, isCalendarBlocked, hasAcupunctureLicense, yearmonth, consents]
  );

  // BD §7.5: disable if no 免許きゅう OR consent does not include きゅう treatment content
  const isMoxibustionGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked || !hasMoxibustionLicense) return true;
      const date = buildDate(yearmonth, day);
      return !isConsentTreatmentAllowed(date, consents, 'has_moxibustion');
    },
    [isGreyed, isCalendarBlocked, hasMoxibustionLicense, yearmonth, consents]
  );

  // BD §7.3: disable if future/calendar-blocked OR both はり+きゅう grayed on this specific day
  const isInitialGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      return isAcupunctureGrayed(day) && isMoxibustionGrayed(day);
    },
    [isGreyed, isCalendarBlocked, isAcupunctureGrayed, isMoxibustionGrayed]
  );

  // BD §7.6: disable if consent lacks 電 content, OR both はり+きゅう grayed on this day, OR future date
  const isElectricGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      const date = buildDate(yearmonth, day);
      if (!isConsentTreatmentAllowed(date, consents, 'has_electric')) return true;
      return isAcupunctureGrayed(day) && isMoxibustionGrayed(day);
    },
    [isGreyed, isCalendarBlocked, yearmonth, consents, isAcupunctureGrayed, isMoxibustionGrayed]
  );

  const isR2MassageGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      return !isR2MassageAllowed(day);
    },
    [isGreyed, isCalendarBlocked, isR2MassageAllowed]
  );

  const isR2MassagePartGrayed = useCallback(
    (day: number, part: string): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      return !isR2MassagePartAllowed(day, part);
    },
    [isGreyed, isCalendarBlocked, isR2MassagePartAllowed]
  );

  const isR2OrthomorphiaGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      return !isR2OrthomorphiaAllowed(day);
    },
    [isGreyed, isCalendarBlocked, isR2OrthomorphiaAllowed]
  );

  const isR2OrthomorphiaPartGrayed = useCallback(
    (day: number, part: string): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      return !isR2OrthomorphiaPartAllowed(day, part);
    },
    [isGreyed, isCalendarBlocked, isR2OrthomorphiaPartAllowed]
  );

  // HIT-15106 (BD 1.3.2 S37/S38): 温罨法 needs at least one マッサージ limb that day
  // (変形徒手 alone does not count).
  const hasNoR2MassagePart = useCallback(
    (day: number): boolean => getR2MassagePartSet(day, getRow(day)).size === 0,
    [getR2MassagePartSet, getRow]
  );

  const isR2HotCompressGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked || isLocked(day) || isError(day) || isR2CoreTreatmentDisabled(day)) return true;
      if (hasNoR2MassagePart(day)) return true;
      return !isR2HotTreatmentAllowed(day, R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS);
    },
    [isGreyed, isCalendarBlocked, isLocked, isError, isR2CoreTreatmentDisabled, hasNoR2MassagePart, isR2HotTreatmentAllowed]
  );

  const isR2HotCompressElectroGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked || isLocked(day) || isError(day) || isR2CoreTreatmentDisabled(day)) return true;
      if (hasNoR2MassagePart(day)) return true;
      return !isR2HotTreatmentAllowed(day, R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS_ELECTRO);
    },
    [isGreyed, isCalendarBlocked, isLocked, isError, isR2CoreTreatmentDisabled, hasNoR2MassagePart, isR2HotTreatmentAllowed]
  );

  // BD §7.7: disable when both はり+きゅう are grayed on this specific day
  const isHouseCallGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      if (isR2) return isR2CoreTreatmentDisabled(day);
      return isAcupunctureGrayed(day) && isMoxibustionGrayed(day);
    },
    [isGreyed, isCalendarBlocked, isR2, isR2CoreTreatmentDisabled, isAcupunctureGrayed, isMoxibustionGrayed]
  );

  /**
   * A2 "scheduling" click — triggered by clicking a cell in a day column.
   * - Header click + empty day: auto-populate all non-gray treatment slots with 〇
   * - Click on a treatment-level **gray** cell: select column only — do not 〇 non-gray cells (BD Step)
   * - If day has data: just select (highlight existing 〇)
   */
  const handleDayClick = useCallback(
    (day: number, row: CalendarClickRow = 'header', clickedCellGrayed = false) => {
      if (isCalendarBlocked) return;
      // A2 parity (schedule.blade.php lines 1038-1048): cells that have saved 〇 data
      // always have grayout removed — they remain selectable even if the day later becomes blocked
      // (so the user can click → select → 日別削除 for error visits).
      // Only hard-block when the day has no saved data at all.
      if (isGreyed(day) && !isLocked(day) && !visits[String(day)]) return;

      setSelectedDay(day);

      // Grayed day (future) that has saved data: just select so user can 日別削除.
      // Locked days also just select. A2: no auto-populate in either case.
      if (isLocked(day) || isGreyed(day)) return;

      // If day already has data, do not modify — just select
      if (hasData(day)) return;

      // If the clicked cell itself is gray, do not auto-populate other cells with 〇
      if (clickedCellGrayed) return;

      // 施術グレー行をクリック → 自動入力しない（同列の非グレーに〇が付かないようにする）
      if (row !== 'header') {
        const clickedTreatmentGray =
          (row === 'initial' && isInitialGrayed(day)) ||
          (row === 'acupuncture' && isAcupunctureGrayed(day)) ||
          (row === 'moxibustion' && isMoxibustionGrayed(day)) ||
          (row === 'electric' && isElectricGrayed(day)) ||
          (row === 'r2Massage' && isR2MassageGrayed(day)) ||
          (row === 'r2Orthomorphia' && isR2OrthomorphiaGrayed(day)) ||
          (row === 'r2HotCompress' && isR2HotCompressGrayed(day)) ||
          (row === 'r2HotCompressElectro' && isR2HotCompressElectroGrayed(day)) ||
          ((row === 'houseCall' || row === 'massager') && isHouseCallGrayed(day));
        if (clickedTreatmentGray) return;
      }

      // Auto-populate defaults based on what's available (A2 behavior)
      // is_deleted: false — resurrects a day that was previously marked for deletion
      const date = buildDate(yearmonth, day);
      const isFirstTreatmentDay = isFirstTreatmentDate(date);
      const r2MassageParts = Array.from(getR2MassagePartSet(day, buildDefault(null))).join(',');
      const r2OrthomorphiaParts = Array.from(getR2OrthomorphiaPartSet(day, buildDefault(null))).join(',');
      const update: Partial<PendingVisitData> = {
        is_initial_inspection_fee: !isR2 && isFirstTreatmentDay && !isInitialGrayed(day) ? 1 : 0,
        is_acupuncture: !isR2 && !isAcupunctureGrayed(day) ? 1 : 0,
        is_moxibustion: !isR2 && !isMoxibustionGrayed(day) ? 1 : 0,
        electric_treatment: !isR2 && !isElectricGrayed(day) ? 1 : 0,
        // HIT-15106: no マッサージ limb on the 同意書 (変形徒手 only) → no 温罨法.
        is_not_hot_treatment: isR2 && r2MassageParts === '',
        massage: isR2 ? r2MassageParts : null,
        orthomorphia: isR2 ? r2OrthomorphiaParts : null,
        previous_visit_id: -1,
        massager_id: mainMassagerId ?? 0,
        is_deleted: false,
      };
      updateRow(day, update);
    },
    [
      isCalendarBlocked,
      isGreyed,
      isLocked,
      visits,
      hasData,
      isFirstTreatmentDate,
      isInitialGrayed,
      isAcupunctureGrayed,
      isMoxibustionGrayed,
      isElectricGrayed,
      isR2MassageGrayed,
      isR2OrthomorphiaGrayed,
      isR2HotCompressGrayed,
      isR2HotCompressElectroGrayed,
      isHouseCallGrayed,
      isR2,
      getR2MassagePartSet,
      getR2OrthomorphiaPartSet,
      buildDefault,
      mainMassagerId,
      updateRow,
    ]
  );

  const handleHeaderClick = useCallback(
    (day: number) => {
      if (isCalendarBlocked) return;
      if (isGreyed(day) && !isLocked(day) && !visits[String(day)]) return;

      setSelectedDay(day);
      setIsDetailOpen(true);
    },
    [isCalendarBlocked, isGreyed, isLocked, visits]
  );

  /**
   * Cell class helper — 2-tier grayout model (matches A2 behavior):
   *
   * - Day-level block  (future date / calendar blocked):
   *     → VC_GRAYOUT: full pointer-events-none — cannot click at all.
   *     → Exception: if saved server data exists on a now-blocked day, keep clickable
   *       so user can still select it for 日別削除.
   *
   * - Treatment-level gray  (license or consent content restriction):
   *     → VC_GRAYOUT_SOFT: gray color only, no pointer-events-none.
   *     → User can still click to select the day column → enables 日別削除 button.
   *     → handleDayClick auto-populates with that treatment excluded (already handled).
   *
   * - Selection highlight:
   *     → If day selected + already has saved data → only highlight cells with marks (〇)
   *     → If day selected + no data → highlight entire column
   */
  function cellClass(day: number, treatmentGrayed: boolean, hasMark = false): string {
    const isSel = selectedDay === day;
    const dayFullyBlocked = isGreyed(day) || isCalendarBlocked;
    const hasSavedData = hasMark && !!visits[String(day)];
    const dayHasExistingData = !!visits[String(day)];
    const parts = [TD_BASE, DAY_COL_W];

    // Error days: only color cells with 〇 red. When the user clicks (selected), the 〇 cell changes to
    // blue selection, empty cells are not highlighted.
    if (isError(day) && hasMark) {
      parts.push(isSel ? VC_SELECTED : VC_ERROR);
    } else if (isError(day) && dayFullyBlocked && !isLocked(day)) {
      // Error day on a blocked date, no mark: soft grey — still clickable so user can
      // open 詳細 to inspect error messages (e.g. consent validity errors).
      // hasMark is always false here (hasMark=true is caught by the condition above).
      // A2 behavior: only mark cells (〇) go blue on select — empty cells stay grey.
      parts.push(VC_GRAYOUT_SOFT);
    } else if (dayFullyBlocked && !hasSavedData && !isLocked(day)) {
      // Day-level block: future date / calendar blocked, no saved data, not locked
      parts.push(VC_GRAYOUT);
    } else if (treatmentGrayed && !hasMark && !hasSavedData && !isLocked(day)) {
      // Treatment-level grayout with no mark: should be non-interactive (no click / no selection)
      parts.push(VC_GRAYOUT);
    } else if (treatmentGrayed && !hasSavedData && !isLocked(day)) {
      // Treatment-level gray: show gray color but remain clickable for selection / 日別削除
      if (isSel) parts.push(VC_SELECTED);
      else parts.push(VC_GRAYOUT_SOFT);
    } else {
      // Normal clickable: active day, locked day, or day with saved data
      parts.push(VC_CLICKABLE);
      // A2 behavior: if day already has data, only highlight cells with marks (〇)
      // if day has no data, highlight entire column
      if (isSel && dayHasExistingData && !hasMark) {
        // Selected day with existing data, but this cell has no mark → don't highlight
        // (just show hover effect from VC_CLICKABLE)
      } else if (isSel) {
        parts.push(VC_SELECTED);
      }
    }
    appendSundayColumnBg(parts, yearmonth, day);
    return parts.join(' ');
  }

  // A2 §7.4.8: error days show 〇 in red
  // UAT: when the day has a 転帰 (outcome_type_id) selected, all checked marks
  // in that column render the matching character in red:
  //   1 治癒 → 治, 2 中止 → 中, 3 転医 → 転
  function renderMark(
    checked: boolean,
    error = false,
    outcomeTypeId: number | null = null,
    houseCallMark = '〇'
  ): React.ReactNode {
    if (!checked) return null;
    const tid = outcomeTypeId !== null ? Number(outcomeTypeId) : null;
    const outcomeChar = tid === 1 ? '治' : tid === 2 ? '中' : tid === 3 ? '転' : null;
    if (outcomeChar) {
      return <span className={styles.markError}>{outcomeChar}</span>;
    }
    return <span className={error ? styles.markError : styles.mark}>{houseCallMark}</span>;
  }

  // BD §7.9: 日別削除 — triggered by double-click on the day column. Errors show as
  // custom system dialogs (not toasts/alerts).
  //
  // 往診 is checked BEFORE lock, so a day that is both only ever shows the 訪問入力
  // dialog. That is the order A2 production uses (schedule.blade.js `visit_delete`:
  // the 往診 cell is tested and returns first, the lock test comes after) — and it is
  // the more useful message, since the day cannot be deleted from this screen at all.
  // 一括削除 deliberately reports the other way round; see BD §5.3.
  const handleDayDelete = useCallback(
    (day: number) => {
      const rd = getRow(day);
      const hasHouseCall = rd.previous_visit_id !== null && rd.previous_visit_id !== -1;

      if (hasHouseCall) {
        modal.error({
          title: '訪問入力を削除する場合は訪問画面から削除してください。',
          okText: 'OK',
        });
        return;
      }

      if (isLocked(day)) {
        modal.error({
          title: 'ロックされているため、編集及び削除はできません。',
          okText: 'OK',
        });
        return;
      }

      updateRow(day, { is_deleted: true });
      setSelectedDay(null);
    },
    [isLocked, getRow, updateRow, modal]
  );

  // BD spec: hide entire calendar when no massager is selected
  if (noMassagerSelected) {
    return <p className="p-2 text-xs text-gray-500">主な施術担当者を選択してください。</p>;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className={`${TH_BASE} w-16`} rowSpan={2} colSpan={isR2 ? 2 : 1} />
              {days.map(d => {
                const disabled = isGreyed(d);
                const parts = [
                  TH_BASE,
                  DAY_COL_W,
                  isHoliday(d) ? styles.dowSun : weekdayDowTextClass(yearmonth, d),
                  disabled ? VC_GRAYOUT : VC_CLICKABLE,
                ];
                if (!disabled && selectedDay === d) parts.push(VC_SELECTED);
                else appendSundayColumnBg(parts, yearmonth, d);
                return (
                  <th
                    key={d}
                    data-day={d}
                    data-outcome_type_id={visits[String(d)]?.outcome_type_id ?? ''}
                    data-is_locked={visits[String(d)]?.is_locked ? 'true' : 'false'}
                    className={parts.join(' ')}
                    onClick={() => handleHeaderClick(d)}
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {d}
                  </th>
                );
              })}
            </tr>
            <tr>
              {days.map(d => {
                const disabled = isGreyed(d);
                const parts = [
                  TH_WEEKDAY,
                  DAY_COL_W,
                  isHoliday(d) ? styles.dowSun : weekdayDowTextClass(yearmonth, d),
                  disabled ? VC_GRAYOUT : VC_CLICKABLE,
                ];
                if (!disabled && selectedDay === d) parts.push(VC_SELECTED);
                else appendSundayColumnBg(parts, yearmonth, d);
                return (
                  <th
                    key={d}
                    data-day={d}
                    data-outcome_type_id={visits[String(d)]?.outcome_type_id ?? ''}
                    data-is_locked={visits[String(d)]?.is_locked ? 'true' : 'false'}
                    className={parts.join(' ')}
                    onClick={() => handleHeaderClick(d)}
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {getDayOfWeek(yearmonth, d)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!isR2 && (
              <>
                {/* 初検 */}
                <tr>
                  <td className={`${TD_LABEL} px-2 py-1`} style={{ backgroundColor: ROW_BG.initial }}>
                    初検
                  </td>
                  {days.map(d => {
                    const rd = getRow(d);
                    // HIT-11715: 〇 mirrors the stored flag only (A2 schedule.blade.php:1253),
                    // so an unchecked 初検料 on the 初療日 clears the mark.
                    const checked = !!rd.is_initial_inspection_fee;
                    return (
                      <td
                        key={d}
                        className={cellClass(d, isInitialGrayed(d), checked)}
                        onClick={() => handleDayClick(d, 'initial', isInitialGrayed(d) && !checked)}
                        onDoubleClick={() => handleDayDelete(d)}
                      >
                        {renderMark(
                          checked,
                          isError(d),
                          rd.outcome_type_id ?? null,
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* はり */}
                <tr>
                  {/* A2 .hari: ラベル色は免許なしでも #FFFF99（グレーはセルのみ .grayout） */}
                  <td
                    className={`${TD_LABEL} px-2 py-1`}
                    style={{ backgroundColor: ROW_BG.acupuncture }}
                  >
                    はり
                  </td>
                  {days.map(d => {
                    const checked = !!getRow(d).is_acupuncture;
                    return (
                      <td
                        key={d}
                        className={cellClass(d, isAcupunctureGrayed(d), checked)}
                        onClick={() =>
                          handleDayClick(d, 'acupuncture', isAcupunctureGrayed(d) && !checked)
                        }
                        onDoubleClick={() => handleDayDelete(d)}
                      >
                        {renderMark(
                          checked,
                          isError(d),
                          getRow(d).outcome_type_id ?? null,
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* きゅう */}
                <tr>
                  <td
                    className={`${TD_LABEL} px-2 py-1`}
                    style={{ backgroundColor: ROW_BG.moxibustion }}
                  >
                    きゅう
                  </td>
                  {days.map(d => {
                    const checked = !!getRow(d).is_moxibustion;
                    return (
                      <td
                        key={d}
                        className={cellClass(d, isMoxibustionGrayed(d), checked)}
                        onClick={() =>
                          handleDayClick(d, 'moxibustion', isMoxibustionGrayed(d) && !checked)
                        }
                        onDoubleClick={() => handleDayDelete(d)}
                      >
                        {renderMark(
                          checked,
                          isError(d),
                          getRow(d).outcome_type_id ?? null,
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* 電(鍼・温) */}
                <tr>
                  <td className={`${TD_LABEL} px-2 py-1`} style={{ backgroundColor: ROW_BG.electric }}>
                    電(鍼・温)
                  </td>
                  {days.map(d => {
                    const checked = !!getRow(d).electric_treatment;
                    return (
                      <td
                        key={d}
                        className={cellClass(d, isElectricGrayed(d), checked)}
                        onClick={() => handleDayClick(d, 'electric', isElectricGrayed(d) && !checked)}
                        onDoubleClick={() => handleDayDelete(d)}
                      >
                        {renderMark(
                          checked,
                          isError(d),
                          getRow(d).outcome_type_id ?? null,
                        )}
                      </td>
                    );
                  })}
                </tr>
              </>
            )}

            {isR2 && (
              <>
                {/* マッサージ: vertical group label spanning its part rows (BD sample) */}
                {R2_MASSAGE_PART_OPTIONS.map((part, i) => (
                  <tr key={`r2-massage-${part.value}`}>
                    {i === 0 && (
                      <td
                        rowSpan={R2_MASSAGE_PART_OPTIONS.length}
                        className={R2_GROUP_LABEL}
                        style={{ backgroundColor: R2_ROW_BG.massageGroup }}
                      >
                        <R2GroupLabelText text="マッサージ" />
                      </td>
                    )}
                    <td className={R2_PART_LABEL}>{part.label}</td>
                    {days.map(d => {
                      const rd = getRow(d);
                      const date = buildDate(yearmonth, d);
                      const hasVisitData = !rd.is_deleted && (!!visits[String(d)] || !!pending[date]);
                      const checked =
                        hasVisitData &&
                        getR2MassagePartSet(d, rd).has(part.value);
                      const grayed = isR2MassagePartGrayed(d, part.value);
                      return (
                        <td
                          key={d}
                          className={cellClass(d, grayed, checked)}
                          onClick={() => handleDayClick(d, 'r2MassagePart', grayed && !checked)}
                          onDoubleClick={() => handleDayDelete(d)}
                        >
                          {renderMark(checked, isError(d), rd.outcome_type_id ?? null)}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* 変形徒手矯正: vertical group label spanning its part rows (BD sample) */}
                {R2_ORTHOMORPHIA_PART_OPTIONS.map((part, i) => (
                  <tr key={`r2-orthomorphia-${part.value}`}>
                    {i === 0 && (
                      <td
                        rowSpan={R2_ORTHOMORPHIA_PART_OPTIONS.length}
                        className={R2_GROUP_LABEL}
                        style={{ backgroundColor: R2_ROW_BG.orthomorphiaGroup }}
                      >
                        <R2GroupLabelText text="変形徒手" />
                      </td>
                    )}
                    <td className={R2_PART_LABEL}>{part.label}</td>
                    {days.map(d => {
                      const rd = getRow(d);
                      const date = buildDate(yearmonth, d);
                      const hasVisitData = !rd.is_deleted && (!!visits[String(d)] || !!pending[date]);
                      const checked =
                        hasVisitData &&
                        getR2OrthomorphiaPartSet(d, rd).has(part.value);
                      const grayed = isR2OrthomorphiaPartGrayed(d, part.value);
                      return (
                        <td
                          key={d}
                          className={cellClass(d, grayed, checked)}
                          onClick={() => handleDayClick(d, 'r2OrthomorphiaPart', grayed && !checked)}
                          onDoubleClick={() => handleDayDelete(d)}
                        >
                          {renderMark(checked, isError(d), rd.outcome_type_id ?? null)}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* 温罨法 */}
                <tr>
                  <td colSpan={2} className={R2_PART_LABEL} style={{ backgroundColor: R2_ROW_BG.hotCompress }}>
                    温罨法
                  </td>
                  {days.map(d => {
                    const rd = getRow(d);
                    const date = buildDate(yearmonth, d);
                    const hasVisitData = !rd.is_deleted && (!!visits[String(d)] || !!pending[date]);
                    const checked =
                      hasVisitData &&
                      hasR2ConsentOnDate(d) &&
                      isR2HotTreatmentAllowed(d, R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS) &&
                      !rd.is_not_hot_treatment;
                    return (
                      <td
                        key={d}
                        className={cellClass(d, isR2HotCompressGrayed(d), checked)}
                        onClick={() =>
                          !isR2HotCompressGrayed(d) && handleDayClick(d, 'r2HotCompress')
                        }
                        onDoubleClick={() => handleDayDelete(d)}
                      >
                        {renderMark(checked, isError(d), rd.outcome_type_id ?? null)}
                      </td>
                    );
                  })}
                </tr>

                {/* 温罨法・電療 */}
                <tr>
                  <td
                    colSpan={2}
                    className={R2_PART_LABEL}
                    style={{ backgroundColor: R2_ROW_BG.hotCompressElectro }}
                  >
                    温罨法・電療
                  </td>
                  {days.map(d => {
                    const rd = getRow(d);
                    const date = buildDate(yearmonth, d);
                    const hasVisitData = !rd.is_deleted && (!!visits[String(d)] || !!pending[date]);
                    const checked =
                      hasVisitData &&
                      hasR2ConsentOnDate(d) &&
                      isR2HotTreatmentAllowed(d, R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS_ELECTRO) &&
                      !rd.is_not_hot_treatment;
                    return (
                      <td
                        key={d}
                        className={cellClass(d, isR2HotCompressElectroGrayed(d), checked)}
                        onClick={() =>
                          !isR2HotCompressElectroGrayed(d) && handleDayClick(d, 'r2HotCompressElectro')
                        }
                        onDoubleClick={() => handleDayDelete(d)}
                      >
                        {renderMark(checked, isError(d), rd.outcome_type_id ?? null)}
                      </td>
                    );
                  })}
                </tr>
              </>
            )}

            {/* 往診 */}
            <tr>
              <td
                colSpan={isR2 ? 2 : 1}
                className={isR2 ? R2_PART_LABEL : `${TD_LABEL} px-2 py-1`}
                style={{ backgroundColor: isR2 ? R2_ROW_BG.houseCall : ROW_BG.houseCall }}
              >
                往診
              </td>
              {days.map(d => {
                const rd = getRow(d);
                const isHouseCall = rd.previous_visit_id !== null && rd.previous_visit_id !== -1;
                return (
                  <td
                    key={d}
                    className={cellClass(d, isHouseCallGrayed(d), isHouseCall)}
                    onClick={() => {
                      if (isHouseCallGrayed(d) && !isHouseCall) return;
                      handleDayClick(d, 'houseCall');
                    }}
                    onDoubleClick={() => {
                      if (isHouseCallGrayed(d) && !isHouseCall) return;
                      handleDayDelete(d);
                    }}
                  >
                    {renderMark(
                      isHouseCall,
                      isError(d),
                      rd.outcome_type_id ?? null,
                      getVisitClinicHouseCallMark(rd, buildDate(yearmonth, d)),
                    )}
                  </td>
                );
              })}
            </tr>

            {/* 施術師 — A2: same scheduling handler; per-day massager change only via 詳細 modal */}
            <tr>
              <td colSpan={isR2 ? 2 : 1} className={isR2 ? R2_PART_LABEL : `${TD_LABEL} px-2 py-1`}>
                施術師
              </td>
              {days.map(d => {
                const rd = getRow(d);
                const isDiff = rd.massager_id !== 0 && rd.massager_id !== (mainMassagerId ?? 0);
                return (
                  <td
                    key={d}
                    className={cellClass(d, isHouseCallGrayed(d), isDiff)}
                    onClick={() => handleDayClick(d, 'massager', isHouseCallGrayed(d) && !isDiff)}
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {isDiff ? (
                      (() => {
                        const diffMassager = massagers.find(
                          m =>
                            m.massager_id === rd.massager_id &&
                            m.clinic_location_id === rd.clinic_location_id,
                        );
                        const tooltipText = (() => {
                          if (!diffMassager) return '';
                          // Legacy `.address` was replaced by address1/2/3 + location_name;
                          // fall back to `.address` only when the new fields are absent.
                          const parts = [diffMassager.address1, diffMassager.address2, diffMassager.address3]
                            .filter(Boolean)
                            .join('');
                          const locationName = diffMassager.location_name ?? '';
                          const composed = `${parts}${locationName ? `　${locationName}` : ''}`;
                          const addressText = composed || diffMassager.address || '';
                          return `${diffMassager.name}：${addressText}`;
                        })();
                        return (
                          <AppTooltip title={tooltipText}>
                            <span className={isError(d) ? styles.markError : 'text-black'}>
                              他
                            </span>
                          </AppTooltip>
                        );
                      })()
                    ) : (
                      ''
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-end gap-2 mt-1">
        <AppButton
          type="primary"
          disabled={storeLoading}
          onClick={() => {
            if (selectedDay !== null) setIsDetailOpen(true);
          }}
        >
          詳細
        </AppButton>
      </div>

      {selectedDay !== null && (
        <VisitClinicDetailModal
          patientId={patientId}
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          date={buildDate(yearmonth, selectedDay)}
          current={getRow(selectedDay)}
          rawVisit={visits[String(selectedDay)] ?? null}
          stagedVisits={stagedVisits}
          deletedVisitIds={stagedDeletedVisitIds}
          massagers={massagers}
          outcomeTypes={outcomeTypes ?? []}
          consents={consents}
          rezepts={rezepts}
          isFirstTreatmentDay={isFirstTreatmentDate(buildDate(yearmonth, selectedDay))}
          initialInspectionPrices={initialInspectionPrices}
          onApply={update => updateRow(selectedDay, update)}
          serviceType={serviceType}
          disabled={isLocked(selectedDay) || isGreyed(selectedDay)}
          isLocked={isLocked(selectedDay)}
        />
      )}
    </div>
  );
};

export default VisitClinicCalendarTable;
